import { t } from 'i18next';
import {
  DndChild,
  DndColumn,
  DndItemMapping,
  DndParent,
  DndParentEntry,
  DndSelectorOutput,
  COL_EXCLUDED,
  COL_SOURCE,
  COL_TARGET
} from './types';

// ─── Initialisation des colonnes ──────────────────────────────────────────────

type BuildDndColumnsParams = {
  parents: DndParent[];
  authorisedParents?: number[];
  excludedParents?: number[];
  /** IDs d'enfants partiellement autorisés (parent partiel dans colonne cible). */
  authorisedChildren?: number[];
  /** IDs d'enfants partiellement exclus (parent partiel dans colonne excluded). */
  excludedChildren?: number[];
  searchValue?: string;
  parentsFromSearch?: DndParent[];
  numColumns: 1 | 2 | 3;
  titles?: { source?: string; target?: string; excluded?: string };
};

export function buildDndColumns({
  parents,
  authorisedParents = [],
  excludedParents = [],
  authorisedChildren = [],
  excludedChildren = [],
  searchValue,
  parentsFromSearch,
  numColumns,
  titles
}: BuildDndColumnsParams): DndColumn[] {
  const targetColumn: DndColumn = {
    id: COL_TARGET,
    title: titles?.target ?? t('authorized'),
    items: []
  };
  const excludedColumn: DndColumn = {
    id: COL_EXCLUDED,
    title: titles?.excluded ?? t('unauthorized'),
    items: []
  };
  const sourceColumn: DndColumn = {
    id: COL_SOURCE,
    title: titles?.source ?? t('source'),
    items: []
  };

  const parentsMap = new Map<number, DndParent>();
  parents.forEach((p) => parentsMap.set(p.id, p));

  // Parents entièrement autorisés → colonne cible
  authorisedParents.forEach((parentId) => {
    const parent = parentsMap.get(parentId);
    if (!parent) return;
    targetColumn.items.push({ ...parent, isFullParent: true });
  });

  // Enfants partiellement autorisés → parent partiel dans colonne cible
  authorisedChildren.forEach((childId) => {
    const parentFull = parents.find((p) => p.children.some((c) => c.id === childId));
    if (!parentFull) return;
    const child = parentFull.children.find((c) => c.id === childId);
    if (!child) return;
    const existing = targetColumn.items.find((item) => item.id === parentFull.id);
    if (existing) {
      if (!existing.children.some((c) => c.id === childId)) existing.children.push(child);
    } else {
      targetColumn.items.push({ ...parentFull, children: [child], isFullParent: false });
    }
  });

  // Parents entièrement exclus → colonne excluded
  excludedParents.forEach((parentId) => {
    const parent = parentsMap.get(parentId);
    if (!parent) return;
    excludedColumn.items.push({ ...parent, isFullParent: true });
  });

  // Enfants partiellement exclus → parent partiel dans colonne excluded
  excludedChildren.forEach((childId) => {
    const parentFull = parents.find((p) => p.children.some((c) => c.id === childId));
    if (!parentFull) return;
    const child = parentFull.children.find((c) => c.id === childId);
    if (!child) return;
    const existing = excludedColumn.items.find((item) => item.id === parentFull.id);
    if (existing) {
      if (!existing.children.some((c) => c.id === childId)) existing.children.push(child);
    } else {
      excludedColumn.items.push({ ...parentFull, children: [child], isFullParent: false });
    }
  });

  // Recalculer isFullParent pour target et excluded
  [targetColumn, excludedColumn].forEach((column) => {
    column.items.forEach((item) => {
      const fullParent = parentsMap.get(item.id);
      if (fullParent) {
        item.isFullParent =
          item.children.length === (fullParent.childrenLength ?? fullParent.children.length);
      }
    });
  });

  // Colonne unique : seul le contenu de la cible est affiché, la source n'a pas lieu d'être
  // construite (elle distribuerait tout le référentiel pour rien).
  if (numColumns === 1) {
    return [{ ...targetColumn, items: targetColumn.items.sort(compareDndItems) }];
  }

  // Construire la colonne source (items neutres / non encore distribués)
  const parentsToProcess = searchValue && parentsFromSearch ? parentsFromSearch : parents;
  parentsToProcess.forEach((searchParent) => {
    const fullParent = parentsMap.get(searchParent.id);
    if (!fullParent) return;

    const isFlat = fullParent.children.length === 0 && (fullParent.childrenLength ?? 0) === 0;

    if (isFlat) {
      // Mode flat : item atomique — en source uniquement s'il n'est ni en target ni en excluded
      const inTarget = targetColumn.items.some((item) => item.id === fullParent.id);
      const inExcluded = excludedColumn.items.some((item) => item.id === fullParent.id);
      if (!inTarget && !inExcluded) {
        sourceColumn.items.push({ ...fullParent, isFullParent: true });
      }
    } else {
      // Mode hiérarchique : enfants non encore dans target ou excluded
      const childrenInTarget = new Set<number>();
      const childrenInExcluded = new Set<number>();
      targetColumn.items
        .filter((item) => item.id === searchParent.id)
        .forEach((item) => item.children.forEach((c) => childrenInTarget.add(c.id)));
      excludedColumn.items
        .filter((item) => item.id === searchParent.id)
        .forEach((item) => item.children.forEach((c) => childrenInExcluded.add(c.id)));

      const neutralChildren = fullParent.children.filter(
        (child) => !childrenInTarget.has(child.id) && !childrenInExcluded.has(child.id)
      );
      if (neutralChildren.length > 0) {
        const totalChildren = fullParent.childrenLength ?? fullParent.children.length;
        sourceColumn.items.push({
          ...fullParent,
          children: neutralChildren,
          isFullParent: neutralChildren.length === totalChildren,
          childrenLength: totalChildren
        });
      }
    }
  });

  // Supprimer de source les parents entièrement traités (full dans target ou excluded)
  const fullParentsElsewhere = new Set<number>([
    ...targetColumn.items.filter((item) => item.isFullParent).map((item) => item.id),
    ...excludedColumn.items.filter((item) => item.isFullParent).map((item) => item.id)
  ]);
  sourceColumn.items = sourceColumn.items.filter((item) => !fullParentsElsewhere.has(item.id));

  return numColumns === 3
    ? [targetColumn, sourceColumn, excludedColumn]
    : [sourceColumn, targetColumn];
}

// ─── Mutations de colonnes ────────────────────────────────────────────────────

export function findParent(column: DndColumn, parentId: number): DndParentEntry | undefined {
  return column.items.find((p) => p.id === parentId);
}

export function removeChildren(column: DndColumn, parentId: number, childIds: number[]): DndColumn {
  return {
    ...column,
    items: column.items
      .map((parent) => {
        if (parent.id !== parentId) return parent;
        const remaining = parent.children.filter((c) => !childIds.includes(c.id));
        return { ...parent, children: remaining, isFullParent: false };
      })
      // Retirer uniquement le parent modifié s'il n'a plus d'enfants (les items flat non modifiés ont children:[] et ne doivent pas être retirés)
      .filter((parent) => parent.id !== parentId || parent.children.length > 0)
  };
}

export function addChildren(
  column: DndColumn,
  parent: Pick<DndParentEntry, 'id' | 'label'>,
  childrenToAdd: DndChild[],
  allChildrenCount: number
): DndColumn {
  const existing = findParent(column, parent.id);
  if (!existing) {
    return {
      ...column,
      items: [
        ...column.items,
        {
          id: parent.id,
          label: parent.label,
          children: childrenToAdd,
          childrenLength: allChildrenCount,
          isFullParent: childrenToAdd.length === allChildrenCount
        }
      ]
    };
  }
  const merged = [
    ...existing.children,
    ...childrenToAdd.filter((c) => !existing.children.some((ec) => ec.id === c.id))
  ];
  return {
    ...column,
    items: column.items.map((p) =>
      p.id === parent.id
        ? { ...p, children: merged, isFullParent: merged.length === allChildrenCount }
        : p
    )
  };
}

// ─── Tri et filtre ────────────────────────────────────────────────────────────

export const compareDndItems = (a: DndParentEntry, b: DndParentEntry): number =>
  a.label.trim().localeCompare(b.label.trim());

export function filterDndItems(items: DndParentEntry[], searchValue: string): DndParentEntry[] {
  if (!searchValue.trim()) return items;
  const lower = searchValue.toLowerCase();
  return items
    .map((parent) => {
      // On filtre sur les deux libellés affichés : le code (oss_code) et le libellé (oss_label).
      const filteredChildren = parent.children.filter(
        (child) =>
          child.primaryLabel.toLowerCase().includes(lower) ||
          !!child.secondaryLabel?.toLowerCase().includes(lower)
      );
      if (parent.label.toLowerCase().includes(lower) || filteredChildren.length > 0) {
        return {
          ...parent,
          children: filteredChildren.length > 0 ? filteredChildren : parent.children
        };
      }
      return null;
    })
    .filter(Boolean) as DndParentEntry[];
}

// ─── Application des drags ────────────────────────────────────────────────────

export function applyDndSingleDrag(
  columns: DndColumn[],
  type: 'parent' | 'child',
  element: DndParentEntry,
  sourceColumnId: string,
  targetColumnId: string,
  allParents: DndParent[]
): DndColumn[] {
  const sourceColumn = columns.find((c) => c.id === sourceColumnId);
  if (!sourceColumn) return columns;
  const targetColumn = columns.find((c) => c.id === targetColumnId);
  if (!targetColumn) return columns;

  const fullParent = allParents.find((p) => p.id === element.id);
  if (!fullParent) return columns;

  const allChildrenCount = fullParent.childrenLength ?? fullParent.children.length;

  let newSource = sourceColumn;
  let newTarget = targetColumn;

  if (type === 'parent') {
    newSource = removeChildren(
      sourceColumn,
      element.id,
      element.children.map((c) => c.id)
    );
    newTarget = addChildren(targetColumn, element, element.children, allChildrenCount);
  } else {
    const draggedChild = element.children[0];
    newSource = removeChildren(sourceColumn, element.id, [draggedChild.id]);
    newTarget = addChildren(targetColumn, element, [draggedChild], allChildrenCount);
  }

  return columns.map((col) => {
    let res = col;
    if (col.id === sourceColumnId) res = newSource;
    if (col.id === targetColumnId) res = newTarget;
    return { ...res, items: res.items.sort(compareDndItems) };
  });
}

export function applyDndMultiDrag(
  columns: DndColumn[],
  selection: Set<string>,
  targetColumnId: string,
  allParents: DndParent[],
  parseKey: (key: string) => { columnId: string; type: string; itemId: number }
): DndColumn[] {
  let result = [...columns];

  const selectedParentKeys = new Set<string>();
  selection.forEach((key) => {
    const { columnId, type, itemId } = parseKey(key);
    if (type === 'parent') selectedParentKeys.add(`${columnId}||${itemId}`);
  });

  selection.forEach((key) => {
    const { columnId: itemColumnId, type: itemType, itemId } = parseKey(key);
    if (itemColumnId === targetColumnId) return;

    const sourceColIdx = result.findIndex((c) => c.id === itemColumnId);
    const targetColIdx = result.findIndex((c) => c.id === targetColumnId);
    if (sourceColIdx === -1 || targetColIdx === -1) return;

    const sourceCol = result[sourceColIdx];
    const targetCol = result[targetColIdx];

    if (itemType === 'parent') {
      const parentEntry = sourceCol.items.find((item) => item.id === itemId);
      if (!parentEntry) return;

      const fullParent = allParents.find((p) => p.id === itemId);
      const allChildrenCount = fullParent?.childrenLength ?? fullParent?.children.length ?? 0;

      const newSource = removeChildren(
        sourceCol,
        itemId,
        parentEntry.children.map((c) => c.id)
      );
      const newTarget = addChildren(targetCol, parentEntry, parentEntry.children, allChildrenCount);

      result = result.map((col, i) =>
        i === sourceColIdx ? newSource : i === targetColIdx ? newTarget : col
      );
    } else {
      const parentEntry = result[sourceColIdx].items.find((item) =>
        item.children.some((c) => c.id === itemId)
      );
      if (!parentEntry) return;
      if (selectedParentKeys.has(`${itemColumnId}||${parentEntry.id}`)) return;

      const child = parentEntry.children.find((c) => c.id === itemId);
      if (!child) return;

      const fullParent = allParents.find((p) => p.id === parentEntry.id);
      const allChildrenCount = fullParent?.childrenLength ?? fullParent?.children.length ?? 0;

      const newSource = removeChildren(sourceCol, parentEntry.id, [itemId]);
      const newTarget = addChildren(targetCol, parentEntry, [child], allChildrenCount);

      result = result.map((col, i) =>
        i === sourceColIdx ? newSource : i === targetColIdx ? newTarget : col
      );
    }
  });

  return result.map((col) => ({ ...col, items: col.items.sort(compareDndItems) }));
}

// ─── Mapping données domaine → DndParent ─────────────────────────────────────

export function mapDataToParents<T>(data: T[], mapping: DndItemMapping<T>): DndParent[] {
  return data.map((item) => ({
    id: mapping.getId(item),
    label: mapping.getLabel(item),
    children: mapping.getChildren ? mapping.getChildren(item) : [],
    childrenLength: mapping.getChildrenLength ? mapping.getChildrenLength(item) : undefined
  }));
}

// ─── Extraction de l'output structuré ────────────────────────────────────────

export function extractSelectorOutput(columns: DndColumn[]): DndSelectorOutput {
  const byColumn: DndSelectorOutput['byColumn'] = {};
  columns.forEach((col) => {
    if (col.id === COL_SOURCE) return;
    const fullParentIds: number[] = [];
    const partialChildIds: number[] = [];
    col.items.forEach((item) => {
      if (item.isFullParent) {
        fullParentIds.push(item.id);
      } else {
        item.children.forEach((child) => partialChildIds.push(child.id));
      }
    });
    byColumn[col.id] = { fullParentIds, partialChildIds };
  });
  return { byColumn };
}
